import type { UserProfile } from '@calling/shared';
import { API_BASE, isNative, platform } from './platform';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSec = 0,
  ) {
    super(message);
  }
}

/** The request never reached the server (offline, DNS, server down). */
export class NetworkError extends Error {
  constructor() {
    super("Can't reach the server. Check your connection and try again.");
  }
}

export interface SessionState {
  status: 'loading' | 'signedOut' | 'signedIn';
  user: UserProfile | null;
  /**
   * Why this device was just signed out, when the server said why: a super
   * admin suspended the account. The sign-in screen explains it until it is
   * dismissed or someone signs in.
   */
  endedBy?: 'account_suspended';
}

/** What the server answers a device that signs in: the refresh token comes in the answer only to the apps (the website keeps it in a cookie). */
export interface SignInResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  user: UserProfile;
}
type RefreshResponse = SignInResponse;

const REFRESH_KEY = 'calling.refresh';

/** Refresh-token storage for the native apps: Keychain on iOS, Keystore-backed on Android. */
const secureStore = {
  async get(): Promise<string | null> {
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
      return (await SecureStoragePlugin.get({ key: REFRESH_KEY })).value;
    } catch {
      return null;
    }
  },
  async set(value: string): Promise<void> {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.set({ key: REFRESH_KEY, value });
  },
  async clear(): Promise<void> {
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
      await SecureStoragePlugin.remove({ key: REFRESH_KEY });
    } catch {
      /* nothing stored */
    }
  },
};

type Listener = (state: SessionState) => void;

/**
 * Talks to the API and owns the signed-in session.
 *
 * The access token lives only in memory. On the web the refresh token is an
 * httpOnly cookie the page cannot read; in the native apps it is kept in
 * secure storage and sent in the request body.
 */
export class Api {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private refreshing: Promise<boolean> | null = null;
  private timer: number | null = null;
  private state: SessionState = { status: 'loading', user: null };
  private readonly listeners = new Set<Listener>();

  getState = (): SessionState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private setState(state: SessionState): void {
    this.state = state;
    for (const l of this.listeners) l(state);
  }

  /** Restores the session when the app opens. */
  async init(): Promise<void> {
    await this.refresh();
  }

  /** A valid access token, refreshed if it is about to expire; null when signed out. */
  async token(): Promise<string | null> {
    if (this.state.status === 'signedOut') return null;
    if (!this.accessToken || Date.now() > this.expiresAt - 30_000) await this.refresh();
    return this.accessToken;
  }

  private adopt(res: RefreshResponse): void {
    this.accessToken = res.accessToken;
    this.expiresAt = Date.now() + res.expiresIn * 1000;
    this.setState({ status: 'signedIn', user: res.user });
    if (this.timer !== null) window.clearTimeout(this.timer);
    // Renew a minute early so a call in progress never holds an expired token.
    this.timer = window.setTimeout(() => void this.refresh(), Math.max(10_000, (res.expiresIn - 60) * 1000));
  }

  private signedOut(endedBy?: SessionState['endedBy']): void {
    this.accessToken = null;
    this.expiresAt = 0;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.setState({ status: 'signedOut', user: null, ...(endedBy ? { endedBy } : {}) });
  }

  /** The person read why they were signed out (SessionState.endedBy). */
  dismissEnded(): void {
    if (this.state.endedBy) this.setState({ status: this.state.status, user: this.state.user });
  }

  /** One refresh at a time, however many requests ask for it. */
  refresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    // The server sets a readable flag next to the httpOnly refresh cookie.
    // Without it there is no session to restore, so guests skip the request.
    if (!isNative && !document.cookie.split('; ').includes('calling_session=1')) {
      this.signedOut();
      return false;
    }
    try {
      let body: string | undefined;
      if (isNative) {
        const refreshToken = await secureStore.get();
        if (!refreshToken) {
          this.signedOut();
          return false;
        }
        body = JSON.stringify({ refreshToken });
      }
      const res = await this.raw('/api/auth/refresh', { method: 'POST', body });
      const data = (await res.json()) as RefreshResponse;
      if (isNative && data.refreshToken) await secureStore.set(data.refreshToken);
      this.adopt(data);
      return true;
    } catch (err) {
      const rejected = err instanceof ApiError && (err.status === 401 || err.status === 403);
      if (!rejected) {
        // Offline or a server fault is not the same as signed out: keep what
        // we have and let the caller retry.
        if (this.state.status === 'loading') this.setState({ status: this.accessToken ? 'signedIn' : 'signedOut', user: this.state.user });
        return false;
      }
      if (isNative) await secureStore.clear();
      this.signedOut(err.code === 'account_suspended' ? 'account_suspended' : undefined);
      return false;
    }
  }

  /** Finishes a native sign-in by redeeming the one-time code from the deep link. */
  async exchangeNativeCode(code: string, verifier: string): Promise<void> {
    const res = await this.raw('/api/auth/native/exchange', { method: 'POST', body: JSON.stringify({ code, verifier }) });
    await this.acceptSession((await res.json()) as RefreshResponse);
  }

  /** Takes a session the server just gave this device: the apps keep its refresh token in secure storage. */
  async acceptSession(res: SignInResponse): Promise<void> {
    if (isNative && res.refreshToken) await secureStore.set(res.refreshToken);
    this.adopt(res);
  }

  /** Finishes a sign-in with the iPhone's Sign in with Apple sheet (appleSignIn.ts). Returns how it went: created, returning or linked. */
  async finishAppleSheet(answer: { identityToken: string; authorizationCode: string; givenName: string | null; familyName: string | null }): Promise<string> {
    const res = await this.raw('/api/auth/apple/native', { method: 'POST', body: JSON.stringify(answer) });
    const data = (await res.json()) as SignInResponse & { outcome: string };
    await this.acceptSession(data);
    return data.outcome;
  }

  /** Signs in with an email address and a password, on the website or in the apps (no browser involved). */
  async signInWithPassword(email: string, password: string): Promise<UserProfile> {
    const res = await this.raw('/api/auth/password/signin', { method: 'POST', body: JSON.stringify({ email: email.trim(), password, platform }) });
    const data = (await res.json()) as SignInResponse;
    await this.acceptSession(data);
    return data.user;
  }

  async signOut(): Promise<void> {
    try {
      const refreshToken = isNative ? await secureStore.get() : undefined;
      await this.raw('/api/auth/logout', { method: 'POST', body: refreshToken ? JSON.stringify({ refreshToken }) : undefined });
    } catch {
      // Signing out locally must work even when the server cannot be reached.
    }
    if (isNative) await secureStore.clear();
    this.signedOut();
  }

  /** Called after the account was deleted on the server. */
  async forget(): Promise<void> {
    if (isNative) await secureStore.clear();
    this.signedOut();
  }

  setUser(user: UserProfile): void {
    if (this.state.status === 'signedIn') this.setState({ status: 'signedIn', user });
  }

  private async raw(path: string, init: { method?: string; body?: string; token?: string | null }): Promise<Response> {
    const headers: Record<string, string> = { 'x-requested-with': 'calling' };
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    if (init.token) headers.authorization = `Bearer ${init.token}`;
    let res: Response;
    try {
      res = await fetch(API_BASE + path, { method: init.method ?? 'GET', headers, body: init.body, credentials: API_BASE ? 'omit' : 'same-origin' });
    } catch {
      throw new NetworkError();
    }
    if (res.ok) return res;
    let code = 'error';
    let message = 'Something went wrong. Try again.';
    try {
      const data = (await res.json()) as { error?: { code?: string; message?: string } };
      code = data.error?.code ?? code;
      message = data.error?.message ?? message;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, code, message, Number(res.headers.get('retry-after') ?? 0));
  }

  /** `auth: 'optional'` sends the token when signed in, so guests can call the same endpoints. */
  async request<T>(path: string, options: { method?: string; body?: unknown; auth?: 'required' | 'optional' | 'none' } = {}): Promise<T> {
    const auth = options.auth ?? 'required';
    const body = options.body === undefined ? undefined : JSON.stringify(options.body);
    const send = async () => {
      const token = auth === 'none' ? null : await this.token();
      if (auth === 'required' && !token) throw new ApiError(401, 'unauthorized', 'Sign in to continue.');
      return this.raw(path, { method: options.method, body, token });
    };
    let res: Response;
    try {
      res = await send();
    } catch (err) {
      // The token expired or the session was rotated elsewhere: refresh once and retry.
      if (!(err instanceof ApiError) || err.status !== 401 || auth === 'none' || !['token_expired', 'token_invalid'].includes(err.code)) {
        // Signed out on the server (another device signed this one out), or the account was suspended: signed out here too, saying why.
        if (err instanceof ApiError && err.status === 401 && err.code === 'session_revoked') this.signedOut();
        if (err instanceof ApiError && err.status === 401 && err.code === 'account_suspended') {
          if (isNative) await secureStore.clear();
          this.signedOut('account_suspended');
        }
        throw err;
      }
      this.accessToken = null;
      if (!(await this.refresh()) && auth === 'required') throw err;
      res = await send();
    }
    if (res.status === 204) return undefined as T;
    const type = res.headers.get('content-type') ?? '';
    return (type.includes('application/json') ? await res.json() : await res.text()) as T;
  }
}

export const api = new Api();

function randomString(bytes: number): string {
  const data = crypto.getRandomValues(new Uint8Array(bytes));
  return btoa(String.fromCharCode(...data)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));
  return btoa(String.fromCharCode(...digest)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The secret that only this app knows during a native sign-in, which it needs
 * to redeem the code the system browser hands back. Kept in memory, and in
 * the app's own storage too: Android may stop the app while the browser is
 * in front, and the app that the browser then opens is a new one.
 */
let pendingVerifier: string | null = null;
const VERIFIER_KEY = 'calling.signin';
/** A sign-in left longer than this is not finished by the code that comes back: the server's code lasts a minute, its sign-in ten. */
export const VERIFIER_TTL_MS = 10 * 60_000;

export const takeNativeVerifier = (): string | null => {
  let verifier = pendingVerifier;
  pendingVerifier = null;
  try {
    const saved = JSON.parse(localStorage.getItem(VERIFIER_KEY) ?? 'null') as { verifier?: unknown; at?: unknown } | null;
    localStorage.removeItem(VERIFIER_KEY);
    const age = typeof saved?.at === 'number' ? Date.now() - saved.at : Infinity;
    if (!verifier && typeof saved?.verifier === 'string' && age >= 0 && age < VERIFIER_TTL_MS) verifier = saved.verifier;
  } catch {
    // No storage, or something unreadable in it: only the copy in memory counts.
  }
  return verifier;
};

/**
 * Starts sign-in with Google, Microsoft or Apple. The browser leaves the
 * app, the person signs in on the provider's own page, and comes back to
 * `returnTo`. In the iPhone and iPad app, Apple's sign-in is the system's own
 * sheet instead, when the server says so (`iosNative`), and the page stays.
 */
export async function startSignIn(provider: 'google' | 'microsoft' | 'apple', returnTo: string, opts: { iosNative?: boolean } = {}): Promise<void> {
  if (provider === 'apple' && platform === 'ios' && opts.iosNative) {
    const { signInWithAppleSheet } = await import('./appleSignIn');
    return signInWithAppleSheet();
  }
  const params = new URLSearchParams({ return_to: returnTo });
  if (!isNative) {
    window.location.assign(`/api/auth/${provider}/start?${params}`);
    return;
  }
  // Native: sign-in happens in the system browser, which then hands a
  // one-time code back to the app. Only this app instance holds the verifier.
  pendingVerifier = randomString(48);
  try {
    localStorage.setItem(VERIFIER_KEY, JSON.stringify({ verifier: pendingVerifier, at: Date.now() }));
  } catch {
    // Without storage, sign-in still works as long as the app is not stopped meanwhile.
  }
  params.set('platform', platform);
  params.set('app_challenge', await sha256Base64Url(pendingVerifier));
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url: `${API_BASE}/api/auth/${provider}/start?${params}` });
}
